import { yupResolver } from '@hookform/resolvers/yup';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { QA_SUPPOSED_RATING } from '../../common/constants/qaSupposedRating';

import dayjs from 'dayjs';
import { useAccess } from '../../common/hooks/useAccess';
import { NoraPermissionV2, Roles } from '../../common/constants/roles';
import { FetchService } from '../../common/fetch.service';
import {
  QACaseForm,
  QACaseListResponse,
  QACaseResponseDto,
  QAOutcome,
  UpdateQACaseDto,
} from '../../common/interfaces/IQaCase';
import { getQACasesEndPoint, updateQACaseEndPoint } from './api.endPoints';
import { fetchTaxonomyOptions, getRulesetOptionsFromDecision } from './helpers';
import { InitiateQAButton } from './InitiateQACaseButton';

// Filter options for large picklists - limits displayed results for performance
const filterOptions = createFilterOptions<string>({
  matchFrom: 'any',
  limit: 100, // Limit displayed options to 100 for performance
  stringify: (option: string) => option,
});

// Validation schema
const qaCaseSchema = yup.object({
  outcome: yup
    .mixed<QAOutcome>()
    .oneOf([
      QAOutcome.ACCURATE,
      QAOutcome.INACCURATE_RULES,
      QAOutcome.INACCURATE_UW_PHILOSOPHY,
      QAOutcome.INACCURATE_RULES_AND_UW_PHILOSOPHY,
      QAOutcome.MISSING_RULES_RULESETS,
      QAOutcome.INELIGIBLE,
    ])
    .required('Outcome is required'),
  supposedRating: yup.string().when('outcome', {
    is: (outcome: QAOutcome) =>
      outcome !== QAOutcome.ACCURATE && outcome !== QAOutcome.INELIGIBLE,
    then: schema => schema.min(1, 'Supposed rating is required'),
    otherwise: schema => schema.optional(),
  }),
  findings: yup.array().when('outcome', {
    is: (outcome: QAOutcome) =>
      outcome === QAOutcome.INACCURATE_RULES ||
      outcome === QAOutcome.INACCURATE_UW_PHILOSOPHY ||
      outcome === QAOutcome.INACCURATE_RULES_AND_UW_PHILOSOPHY,
    then: schema =>
      schema
        .of(
          yup.object({
            rulesetName: yup.string().min(1, 'Ruleset name is required'),
            taxonomy: yup.string().min(1, 'Taxonomy is required'),
            evidenceSource: yup.string().min(1, 'Evidence source is required'),
            supposedRating: yup.string().min(1, 'Supposed rating is required'),
            comments: yup.string().optional(),
          }),
        )
        .min(1, 'At least one finding is required')
        .required('Findings are required'),
    otherwise: schema =>
      schema.when('outcome', {
        is: QAOutcome.MISSING_RULES_RULESETS,
        then: innerSchema =>
          innerSchema
            .of(
              yup.object({
                rulesetName: yup.string().optional(),
                taxonomy: yup.string().min(1, 'Taxonomy is required'),
                evidenceSource: yup
                  .string()
                  .min(1, 'Evidence source is required'),
                supposedRating: yup
                  .string()
                  .min(1, 'Supposed rating is required'),
                comments: yup.string().optional(),
              }),
            )
            .min(1, 'At least one finding is required')
            .required('Findings are required'),
        otherwise: innerSchema => innerSchema.optional(),
      }),
  }),
});

export function QACase({
  policyId,
  product,
  carrier,
}: {
  policyId: number;
  product: string;
  carrier: string;
}) {
  const queryClient = useQueryClient();
  const canEditQaCase = useAccess({
    permission: NoraPermissionV2.EDIT_QA_CASE,
    legacyCheck: r => r.includes(Roles.ADMIN) || r.includes(Roles.UW_STRATEGY_REVIEWER),
  });
  const [openModal, setOpenModal] = useState(false);
  const [editingCase, setEditingCase] = useState<QACaseResponseDto | null>(
    null,
  );

  // Fetch dropdown options when modal is open
  const { data: rulesetOptions = [], isLoading: isLoadingRulesets } = useQuery({
    queryKey: ['rulesetOptions', product, carrier, policyId],
    queryFn: () =>
      getRulesetOptionsFromDecision(product, carrier, policyId.toString()),
    enabled: openModal,
    staleTime: 5 * 60 * 1000,
  });

  const { data: taxonomyOptions = [], isLoading: isLoadingTaxonomies } =
    useQuery({
      queryKey: ['taxonomyOptions'],
      queryFn: () => fetchTaxonomyOptions(),
      enabled: openModal,
      staleTime: 10 * 60 * 1000,
    });

  const optionsLoading = isLoadingRulesets || isLoadingTaxonomies;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<QACaseForm>({
    resolver: yupResolver(qaCaseSchema),
    defaultValues: {
      outcome: QAOutcome.ACCURATE,
      supposedRating: '',
      findings: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'findings',
  });

  const watchedOutcome = watch('outcome');

  // Reset findings when outcome changes to ensure clean state
  useEffect(() => {
    const requiresFindings =
      watchedOutcome === QAOutcome.INACCURATE_RULES ||
      watchedOutcome === QAOutcome.INACCURATE_UW_PHILOSOPHY ||
      watchedOutcome === QAOutcome.INACCURATE_RULES_AND_UW_PHILOSOPHY ||
      watchedOutcome === QAOutcome.MISSING_RULES_RULESETS;

    if (requiresFindings && fields.length === 0) {
      // Add initial finding when switching to an outcome that requires findings
      append({
        rulesetName: '',
        taxonomy: '',
        evidenceSource: '',
        supposedRating: '',
        comments: '',
      });
    } else if (!requiresFindings && fields.length > 0) {
      // Clear findings and supposedRating when switching to Accurate or Ineligible
      setValue('findings', []);
      setValue('supposedRating', '');
    }
  }, [watchedOutcome, fields.length, append, setValue]);

  // Fetch list of QA cases for this policy (default page=1, limit=10)
  const { data: detailQueries, isLoading: isLoadingList } = useQuery({
    queryKey: ['qaCases', policyId],
    queryFn: () =>
      FetchService.getInstance().get<QACaseListResponse>(
        getQACasesEndPoint({
          page: 1,
          limit: 10,
          policyId,
        }),
      ),
    refetchInterval: 5 * 60 * 1000,
    enabled: !!policyId,
  });

  const details: QACaseResponseDto[] = useMemo(() => {
    return detailQueries?.data
      .map(q => q as QACaseResponseDto | undefined)
      .filter(Boolean) as QACaseResponseDto[];
  }, [detailQueries]);

  // Update mutation
  const { mutate: updateCase, isLoading: isUpdating } = useMutation({
    mutationKey: ['updateQACase'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateQACaseDto;
    }) => {
      return FetchService.getInstance().put(updateQACaseEndPoint(id), {
        body: payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qaCases', policyId] });
      setOpenModal(false);
      setEditingCase(null);
    },
  });

  const handleOpenModal = (detail: QACaseResponseDto) => {
    setEditingCase(detail);
    setOpenModal(true);

    reset({
      outcome: (detail.outcome as QAOutcome) || QAOutcome.ACCURATE,
      supposedRating: detail.supposedRating || '',
      findings:
        detail.findings && detail.findings.length > 0
          ? detail.findings.map(f => ({
              rulesetName: f.rulesetName || '',
              taxonomy: f.taxonomy || '',
              evidenceSource: f.evidenceSource || '',
              supposedRating: f.supposedRating || '',
              comments: f.comments || '',
            }))
          : [
              {
                rulesetName: '',
                taxonomy: '',
                evidenceSource: '',
                supposedRating: '',
                comments: '',
              },
            ],
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingCase(null);
    reset();
  };

  const onSubmit = (data: QACaseForm) => {
    if (!editingCase) return;

    // Determine if this outcome requires findings
    const requiresFindings =
      data.outcome === QAOutcome.INACCURATE_RULES ||
      data.outcome === QAOutcome.INACCURATE_UW_PHILOSOPHY ||
      data.outcome === QAOutcome.INACCURATE_RULES_AND_UW_PHILOSOPHY ||
      data.outcome === QAOutcome.MISSING_RULES_RULESETS;

    // Clean up findings: remove fields that are empty strings to avoid backend validation errors
    // The backend DTO uses @IsString() which fails on empty strings, so we only send fields with values
    const cleanedFindings = requiresFindings
      ? data.findings?.map(finding => {
          const cleaned: any = {};

          // For Missing Rules/Rulesets, don't include rulesetName field at all
          if (
            data.outcome !== QAOutcome.MISSING_RULES_RULESETS &&
            finding.rulesetName
          ) {
            cleaned.rulesetName = finding.rulesetName;
          }

          // Only include fields that have actual values (not empty strings)
          if (finding.taxonomy) cleaned.taxonomy = finding.taxonomy;
          if (finding.evidenceSource)
            cleaned.evidenceSource = finding.evidenceSource;
          if (finding.supposedRating)
            cleaned.supposedRating = finding.supposedRating;
          if (finding.comments) cleaned.comments = finding.comments;

          return cleaned;
        })
      : [];

    const payload: UpdateQACaseDto = {
      outcome: data.outcome,
      supposedRating: requiresFindings ? data.supposedRating : undefined,
      findings: cleanedFindings,
    };

    updateCase({ id: editingCase.id, payload });
  };

  const getCaseTypeLabel = (type: string) => {
    return type === 'engine_rules_review' ? 'Pre Issue' : 'Post Issue';
  };

  const getOutcomeColor = (stringOutcome?: string) => {
    if (!stringOutcome) return 'default';
    return stringOutcome === 'accurate' ? 'success' : 'error';
  };

  const getOutcomeLabel = (stringOutcome?: string) => {
    if (!stringOutcome) return '-';
    // split by _ and convert to title case
    stringOutcome = stringOutcome
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return stringOutcome;
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h2">QA Cases</Typography>
        <InitiateQAButton policyId={policyId} product={product} />
      </Box>

      {!isLoadingList && details.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No QA cases found for this policy
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Review Type</TableCell>
                <TableCell>Decision / Findings</TableCell>
                <TableCell>Assigned Reviewer</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingList && (
                <TableRow>
                  <TableCell colSpan={5}>Loading...</TableCell>
                </TableRow>
              )}

              {!isLoadingList &&
                details.length > 0 &&
                details.map(qaCase => (
                  <TableRow key={qaCase.id} hover>
                    <TableCell>
                      <Chip
                        label={getCaseTypeLabel(qaCase.caseType)}
                        color={
                          qaCase.caseType === 'engine_rules_review'
                            ? 'primary'
                            : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getOutcomeLabel(qaCase.outcome as string)}
                        color={getOutcomeColor(qaCase.outcome as string) as any}
                        size="small"
                      />
                      {qaCase.outcome !== QAOutcome.ACCURATE &&
                        qaCase.findings &&
                        qaCase.findings.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ ml: 1, color: 'text.secondary' }}
                          >
                            ({qaCase.findings.length} findings)
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell>
                      {qaCase.assignedTo?.name || 'Unassigned'}
                    </TableCell>
                    <TableCell>
                      {dayjs(qaCase.updatedAt).format('MMM D, YYYY, hh:mm A')}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={canEditQaCase ? '' : 'You do not have permission to edit QA cases'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModal(qaCase)}
                            disabled={!canEditQaCase}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingCase?.outcome ? 'Edit QA Decision' : 'Create QA Decision'}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Controller
                  name="outcome"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.outcome}>
                      <InputLabel>Decision</InputLabel>
                      <Select {...(field as any)} label="Decision">
                        <MenuItem value={QAOutcome.ACCURATE}>Accurate</MenuItem>
                        <MenuItem value={QAOutcome.INACCURATE_RULES}>
                          Inaccurate Rules
                        </MenuItem>
                        <MenuItem value={QAOutcome.INACCURATE_UW_PHILOSOPHY}>
                          Inaccurate UW Philosophy
                        </MenuItem>
                        <MenuItem
                          value={QAOutcome.INACCURATE_RULES_AND_UW_PHILOSOPHY}
                        >
                          Inaccurate Rules and UW Philosophy
                        </MenuItem>
                        <MenuItem value={QAOutcome.MISSING_RULES_RULESETS}>
                          Missing Rules/Rulesets
                        </MenuItem>
                        <MenuItem value={QAOutcome.INELIGIBLE}>
                          Ineligible
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              {/* Case-level supposed rating - only show when outcome requires it */}
              {watchedOutcome !== QAOutcome.ACCURATE &&
                watchedOutcome !== QAOutcome.INELIGIBLE && (
                  <Box>
                    <Controller
                      name="supposedRating"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          options={QA_SUPPOSED_RATING}
                          getOptionLabel={option => option || ''}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Case Supposed Rating"
                              error={!!errors.supposedRating}
                              helperText={errors.supposedRating?.message}
                            />
                          )}
                          onChange={(_, value) => field.onChange(value || '')}
                          value={field.value || null}
                        />
                      )}
                    />
                  </Box>
                )}
            </Box>

            {/* Findings Section - Show for outcomes that require findings */}
            {(watchedOutcome === QAOutcome.INACCURATE_RULES ||
              watchedOutcome === QAOutcome.INACCURATE_UW_PHILOSOPHY ||
              watchedOutcome === QAOutcome.INACCURATE_RULES_AND_UW_PHILOSOPHY ||
              watchedOutcome === QAOutcome.MISSING_RULES_RULESETS) && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Findings
                </Typography>
                {fields.map((fieldData, index) => (
                  <Box
                    key={fieldData.id}
                    sx={{
                      border: '1px solid #e0e0e0',
                      p: 2,
                      mb: 2,
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Finding {index + 1}
                      </Typography>
                      {fields.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => remove(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns:
                            watchedOutcome === QAOutcome.MISSING_RULES_RULESETS
                              ? '1fr'
                              : '1fr 1fr',
                          gap: 2,
                        }}
                      >
                        {/* Only show Ruleset Name for outcomes other than Missing Rules/Rulesets */}
                        {watchedOutcome !==
                          QAOutcome.MISSING_RULES_RULESETS && (
                          <Controller
                            name={`findings.${index}.rulesetName` as const}
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                {...field}
                                options={rulesetOptions}
                                loading={optionsLoading}
                                filterOptions={filterOptions}
                                getOptionLabel={option => option || ''}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    label="Ruleset Name"
                                    placeholder="Type to search..."
                                    error={
                                      !!errors.findings?.[index]?.rulesetName
                                    }
                                    helperText={
                                      errors.findings?.[index]?.rulesetName
                                        ?.message
                                    }
                                  />
                                )}
                                onChange={(_, value) =>
                                  field.onChange(value || '')
                                }
                                value={field.value || null}
                                noOptionsText="No matching rulesets"
                                loadingText="Loading rulesets..."
                              />
                            )}
                          />
                        )}
                        <Controller
                          name={`findings.${index}.taxonomy` as const}
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              options={taxonomyOptions}
                              loading={optionsLoading}
                              filterOptions={filterOptions}
                              getOptionLabel={option => option || ''}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  label="Taxonomy"
                                  placeholder="Type to search..."
                                  error={!!errors.findings?.[index]?.taxonomy}
                                  helperText={
                                    errors.findings?.[index]?.taxonomy?.message
                                  }
                                />
                              )}
                              onChange={(_, value) =>
                                field.onChange(value || '')
                              }
                              value={field.value || null}
                              noOptionsText="No matching taxonomies"
                              loadingText="Loading taxonomies..."
                            />
                          )}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 2,
                        }}
                      >
                        <Controller
                          name={`findings.${index}.evidenceSource` as const}
                          control={control}
                          render={({ field }) => (
                            <FormControl
                              fullWidth
                              error={!!errors.findings?.[index]?.evidenceSource}
                            >
                              <InputLabel>Evidence Source</InputLabel>
                              <Select
                                {...field}
                                label="Evidence Source"
                                value={field.value || ''}
                              >
                                <MenuItem value="Disclosures">
                                  Disclosures
                                </MenuItem>
                                <MenuItem value="RX">RX</MenuItem>
                                <MenuItem value="DX">DX</MenuItem>
                                <MenuItem value="MIB">MIB</MenuItem>
                                <MenuItem value="MVR">MVR</MenuItem>
                                <MenuItem value="TRL">TRL</MenuItem>
                                <MenuItem value="Criminal Records">
                                  Criminal Records
                                </MenuItem>
                              </Select>
                              {errors.findings?.[index]?.evidenceSource && (
                                <FormHelperText>
                                  {
                                    errors.findings?.[index]?.evidenceSource
                                      ?.message
                                  }
                                </FormHelperText>
                              )}
                            </FormControl>
                          )}
                        />
                        <Controller
                          name={`findings.${index}.supposedRating` as const}
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              options={QA_SUPPOSED_RATING}
                              getOptionLabel={option => option || ''}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  label="Supposed Rating"
                                  error={
                                    !!errors.findings?.[index]?.supposedRating
                                  }
                                  helperText={
                                    errors.findings?.[index]?.supposedRating
                                      ?.message
                                  }
                                />
                              )}
                              onChange={(_, value) =>
                                field.onChange(value || '')
                              }
                              value={field.value || null}
                            />
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          name={`findings.${index}.comments` as const}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Comments"
                              fullWidth
                              error={!!errors.findings?.[index]?.comments}
                              helperText={
                                errors.findings?.[index]?.comments?.message
                              }
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() =>
                    append({
                      rulesetName: '',
                      taxonomy: '',
                      evidenceSource: '',
                      supposedRating: '',
                      comments: '',
                    })
                  }
                  startIcon={<AddIcon />}
                >
                  Add Finding
                </Button>
                {errors.findings && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {errors.findings.message as any}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isUpdating}>
              {isUpdating
                ? 'Updating...'
                : editingCase?.outcome
                  ? 'Update'
                  : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
